import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flushSync } from 'react-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import Page from 'src/components/Page';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import chatsArquivoService from 'src/services/chatsArquivoService';
import v1_actionListaSetores from 'src/actions/v1/clientes/v1_actionListaSetores';
import {
    getDivisoes,
    getBoardArquivo,
    getChatsArquivados,
    criarDivisao,
    editarDivisao,
    excluirDivisao,
    reordenarDivisoes,
    desarquivarChat,
    persistBoard,
    setDivisoes,
    dismissAlerta
} from 'src/redux/slices/chat-arquivo';
import {
    setChatInfo,
    setLimparChatInfo,
    getMensagensWhatsappDb
} from 'src/redux/slices/chat';
import v2_actionBuscarChatById from 'src/actions/v2/services/chats/v2_actionBuscarChatById';
import DrawerChatWhatsapp from 'src/sections/@chats/chat/DrawerChatWhatsapp';

import DivisoesSidebar from 'src/sections/@chats/arquivadas/DivisoesSidebar';
import ArquivoColumn from 'src/sections/@chats/arquivadas/ArquivoColumn';
import ModalDivisao from 'src/sections/@chats/arquivadas/ModalDivisao';
import ModalExcluirDivisao from 'src/sections/@chats/arquivadas/ModalExcluirDivisao';
import { CHATS_ARQUIVADAS_DESIGN as D } from 'src/sections/@chats/arquivadas/ChatsArquivadas.design';

export default function ChatsArquivadas() {
    const { t } = useTranslation('chat');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();

    const { divisoes, chatsPorDivisao, totalArquivadas, permissoes, isLoading, alertas } = useSelector(
        (state) => state.chatArquivo
    );

    const [divisaoSelecionada, setDivisaoSelecionada] = useState(null);
    const [busca, setBusca] = useState('');
    const [setores, setSetores] = useState([]);
    const [modalDivisao, setModalDivisao] = useState({ aberto: false, divisao: null });
    const [modalExcluir, setModalExcluir] = useState({ aberto: false, divisao: null });
    const [chatDetalhe, setChatDetalhe] = useState(null);
    const [drawerAberto, setDrawerAberto] = useState(false);

    const notificar = useCallback(
        (mensagem, variant = 'success') =>
            enqueueSnackbar(mensagem, {
                variant,
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            }),
        [enqueueSnackbar]
    );

    useEffect(() => {
        const carregar = async () => {
            const resposta = await dispatch(getDivisoes());
            if (resposta?.data?.length) dispatch(getBoardArquivo(resposta.data));
        };
        carregar();
    }, [dispatch]);

    useEffect(() => {
        if (!user?.clienteId) return;
        v1_actionListaSetores(user.clienteId)
            .then(({ data }) => setSetores(data?.data || []))
            .catch(() => setSetores([]));
    }, [user?.clienteId]);

    // A busca é feita no backend (varre além do que já está carregado no board).
    useEffect(() => {
        if (!divisoes.length) return undefined;
        const timer = setTimeout(() => {
            dispatch(getBoardArquivo(divisoes, busca.trim() ? { busca: busca.trim() } : {}));
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busca]);

    const divisoesVisiveis = useMemo(
        () =>
            divisaoSelecionada
                ? divisoes.filter((d) => `${d._id}` === `${divisaoSelecionada}`)
                : divisoes,
        [divisoes, divisaoSelecionada]
    );

    // Abre a conversa em drawer, como na Supervisão — o usuário não perde o
    // board de vista nem a posição de scroll ao inspecionar um card.
    const handleAbrirChat = async (chat) => {
        dispatch(setLimparChatInfo());
        setChatDetalhe(null);
        setDrawerAberto(true);

        try {
            const res = await v2_actionBuscarChatById(`${chat._id}`, null, user.id);
            setChatDetalhe(res.data);
            dispatch(setChatInfo(res.data));
            // Espera o estado anterior limpar antes de carregar as mensagens,
            // senão o drawer pisca com a conversa antiga (mesmo motivo do
            // setTimeout em SupervisaoLista.js:360).
            await new Promise((resolve) => setTimeout(resolve, 100));
            dispatch(getMensagensWhatsappDb(res.data.sessao, res.data.numeroCliente));
        } catch (error) {
            setDrawerAberto(false);
            notificar(t('archive.board.openChatError', 'Não foi possível abrir a conversa'), 'error');
        }
    };

    // Recarrega divisões e board — usado quando o backend avisa que a conversa
    // mudou de estado por baixo do usuário (409) e a tela ficou mentindo.
    const recarregarBoard = useCallback(async () => {
        const resposta = await dispatch(getDivisoes());
        if (resposta?.data?.length) dispatch(getBoardArquivo(resposta.data));
    }, [dispatch]);

    const handleDesarquivar = async (chat) => {
        try {
            const resposta = await dispatch(desarquivarChat(`${chat._id}`));
            notificar(
                t('archive.board.unarchiveSuccess', '{{name}} voltou para {{destino}}', {
                    name: chat.nomeCliente || t('archive.board.unarchiveDefaultName', 'Conversa'),
                    destino: resposta?.status_destino || t('archive.board.alertDefaultDestino', 'Atendimentos')
                })
            );
            dispatch(getDivisoes());
        } catch (error) {
            if (error?.conflito) {
                // Já saiu do arquivo (provavelmente o cliente respondeu): o card
                // não deve continuar no board.
                notificar(error.mensagem || t('archive.board.conflictAlreadyOut', 'A conversa já saiu do arquivo.'), 'warning');
                await recarregarBoard();
                return;
            }
            notificar(error?.mensagem || t('archive.board.unarchiveError', 'Não foi possível desarquivar a conversa'), 'error');
        }
    };

    const onDragEnd = async (resultado) => {
        const { destination, source, draggableId } = resultado;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const boardAnterior = chatsPorDivisao;
        const origem = Array.from(chatsPorDivisao[source.droppableId] || []);
        const destino =
            source.droppableId === destination.droppableId
                ? origem
                : Array.from(chatsPorDivisao[destination.droppableId] || []);

        const [movido] = origem.splice(source.index, 1);
        if (!movido) return;
        destino.splice(destination.index, 0, movido);

        flushSync(() => {
            dispatch(
                persistBoard({
                    ...chatsPorDivisao,
                    [source.droppableId]: origem,
                    [destination.droppableId]: destino
                })
            );
        });

        try {
            await chatsArquivoService.mover(draggableId, destination.droppableId, destination.index);
            if (source.droppableId !== destination.droppableId) dispatch(getDivisoes());
        } catch (error) {
            dispatch(persistBoard(boardAnterior));
            // Se a conversa saiu do arquivo enquanto era arrastada, o rollback
            // recoloca um card que não existe mais — só o refetch conserta.
            if (error?.conflito) {
                notificar(error.mensagem || t('archive.board.conflictLeftWhileDragging', 'A conversa saiu do arquivo.'), 'warning');
                await recarregarBoard();
                return;
            }
            notificar(t('archive.board.moveError', 'Não foi possível mover a conversa. Tente novamente.'), 'error');
        }
    };

    const handleReordenarDivisoes = async (novaOrdem) => {
        const anterior = divisoes;
        dispatch(setDivisoes(novaOrdem));
        try {
            await dispatch(reordenarDivisoes(novaOrdem.map((d) => `${d._id}`)));
        } catch (error) {
            dispatch(setDivisoes(anterior));
            notificar(t('archive.board.reorderError', 'Não foi possível reordenar as divisões'), 'error');
        }
    };

    const handleSalvarDivisao = async (dados) => {
        if (modalDivisao.divisao) {
            await dispatch(editarDivisao(`${modalDivisao.divisao._id}`, dados));
            notificar(t('archive.board.divisionUpdated', 'Divisão atualizada'));
        } else {
            const resposta = await dispatch(criarDivisao(dados));
            if (resposta?.data?._id) dispatch(getChatsArquivados(`${resposta.data._id}`));
            notificar(t('archive.board.divisionCreated', 'Divisão criada'));
        }
    };

    const handleExcluirDivisao = async (divisaoId, moverPara) => {
        await dispatch(excluirDivisao(divisaoId, moverPara));
        if (`${divisaoSelecionada}` === `${divisaoId}`) setDivisaoSelecionada(null);
        dispatch(getBoardArquivo(divisoes.filter((d) => `${d._id}` !== `${divisaoId}`)));
        notificar(t('archive.board.divisionDeleted', 'Divisão excluída'));
    };

    // TEMPORARIO: bloqueio de acesso desligado a pedido, durante o desenvolvimento.
    // O backend continua exigindo a rule `arquivo.ver` (chatsArquivo.helper.js),
    // entao sem ela as chamadas do board seguem retornando negado.
    // if (!isLoading && permissoes && permissoes.podeVer === false) {
    //     return (
    //         <Page title={t('archive.page.title', 'Conversas arquivadas')}>
    //             <div className={D.empty.container}>
    //                 <div className={D.empty.iconBox}>
    //                     <Iconify icon="eva:lock-outline" width={28} />
    //                 </div>
    //                 <p className={D.empty.title}>{t('archive.page.accessRestrictedTitle', 'Acesso restrito')}</p>
    //                 <p className={D.empty.subtitle}>
    //                     {t('archive.page.accessRestrictedMessage', 'Você não tem permissão para ver as conversas arquivadas. Fale com um administrador.')}
    //                 </p>
    //             </div>
    //         </Page>
    //     );
    // }

    return (
        <Page title={t('archive.page.title', 'Conversas arquivadas')} className="h-full">
            <div className={D.layout.container}>
                <DivisoesSidebar
                    divisoes={divisoes}
                    totalArquivadas={totalArquivadas}
                    divisaoSelecionada={divisaoSelecionada}
                    podeGerenciar={permissoes.podeGerenciarDivisoes}
                    desarquivarAuto={permissoes.desarquivarAuto}
                    onSelecionar={setDivisaoSelecionada}
                    onNovaDivisao={() => setModalDivisao({ aberto: true, divisao: null })}
                    onReordenar={handleReordenarDivisoes}
                />

                <div className={D.layout.main}>
                    <div className={D.layout.content}>
                        {alertas.map((alerta) => (
                            <div key={alerta.chat_id} className={D.alert.container}>
                                <div className={D.alert.iconBox}>
                                    <Iconify icon="eva:flash-outline" width={16} />
                                </div>
                                <p className={D.alert.text}>
                                    <span className={D.alert.strong}>{alerta.nome_cliente || t('archive.board.alertDefaultName', 'Uma conversa')}</span>
                                    {alerta.divisao_nome ? ` ${t('archive.board.alertLeftDivision', 'saiu de {{divisao}} e', { divisao: alerta.divisao_nome })}` : ''} {t('archive.board.alertReturnedTo', 'voltou para')}{' '}
                                    {alerta.status_destino || t('archive.board.alertDefaultDestino', 'Atendimentos')} {t('archive.board.alertNewMessageSuffix', 'após uma nova mensagem.')}
                                </p>
                                <button
                                    type="button"
                                    className={D.alert.close}
                                    onClick={() => dispatch(dismissAlerta(alerta.chat_id))}
                                >
                                    <Iconify icon="eva:close-outline" width={14} />
                                </button>
                            </div>
                        ))}

                        <div className={D.toolbar.container}>
                            <div className={D.toolbar.searchWrapper}>
                                <Iconify icon="eva:search-outline" width={16} className={D.toolbar.searchIcon} />
                                <input
                                    type="text"
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    placeholder={t('archive.toolbar.searchPlaceholder', 'Buscar nas conversas arquivadas...')}
                                    className={D.toolbar.searchInput}
                                />
                            </div>

                        </div>

                        {!divisoes.length ? (
                            <div className={D.empty.container}>
                                <div className={D.empty.iconBox}>
                                    <Iconify icon="eva:archive-outline" width={28} />
                                </div>
                                <p className={D.empty.title}>{t('archive.menu.empty', 'Nenhuma divisão criada')}</p>
                                <p className={D.empty.subtitle}>
                                    {t('archive.board.emptySubtitle', 'Crie divisões para organizar as conversas arquivadas em estágios, como um funil.')}
                                </p>
                                {permissoes.podeGerenciarDivisoes ? (
                                    <button
                                        type="button"
                                        className={D.empty.action}
                                        onClick={() => setModalDivisao({ aberto: true, divisao: null })}
                                    >
                                        {t('archive.board.createFirstDivision', 'Criar primeira divisão')}
                                    </button>
                                ) : user?.level === 'root' ? (
                                    <button
                                        type="button"
                                        className={D.empty.actionButton}
                                        onClick={() => navigate('/dashboard/configuracoes/arquivo-config')}
                                    >
                                        <Iconify icon="eva:settings-2-outline" width={16} />
                                        {t('archive.board.goToSettings', 'Configurar chats arquivados')}
                                    </button>
                                ) : (
                                    <div className={D.empty.hint}>
                                        <Iconify icon="eva:info-outline" width={16} className={D.empty.hintIcon} />
                                        <span>
                                            {t(
                                                'archive.board.askAdminToConfigure',
                                                'Peça a um administrador para criar as divisões do arquivo em Configurações → Arquivamento.'
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <DragDropContext onDragEnd={onDragEnd}>
                                <div className={D.board.container}>
                                    {divisoesVisiveis.map((divisao) => (
                                        <ArquivoColumn
                                            key={`${divisao._id}`}
                                            divisao={divisao}
                                            chats={chatsPorDivisao[`${divisao._id}`] || []}
                                            podeGerenciar={permissoes.podeGerenciarDivisoes}
                                            onAbrirChat={handleAbrirChat}
                                            onDesarquivar={handleDesarquivar}
                                            onEditarDivisao={(d) => setModalDivisao({ aberto: true, divisao: d })}
                                            onExcluirDivisao={(d) => setModalExcluir({ aberto: true, divisao: d })}
                                        />
                                    ))}
                                </div>
                            </DragDropContext>
                        )}
                    </div>
                </div>
            </div>

            <ModalDivisao
                aberto={modalDivisao.aberto}
                divisao={modalDivisao.divisao}
                setores={setores}
                onFechar={() => setModalDivisao({ aberto: false, divisao: null })}
                onSalvar={handleSalvarDivisao}
            />

            <ModalExcluirDivisao
                aberto={modalExcluir.aberto}
                divisao={modalExcluir.divisao}
                divisoes={divisoes}
                onFechar={() => setModalExcluir({ aberto: false, divisao: null })}
                onConfirmar={handleExcluirDivisao}
            />

            {chatDetalhe && (
                <DrawerChatWhatsapp
                    chat={chatDetalhe}
                    open={drawerAberto}
                    onClose={() => {
                        setChatDetalhe(null);
                        setDrawerAberto(false);
                        dispatch(setLimparChatInfo());
                    }}
                    source={'chat.arquivadas'}
                />
            )}
        </Page>
    );
}
